import {
  Box,
  Button,
  ButtonText,
  Heading,
  HStack,
  ScrollView,
  Text,
  VStack,
} from '@gluestack-ui/themed';
import { useMemo } from 'react';
import { NavigationContainer, NavigationIndependentTree } from '@react-navigation/native';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import '@/common/i18n';
import { useTenantSettingsStore } from '@/common/context/TenantSettingsStore';
import { StudentPreviewSourceProvider } from '@/common/studentPreview/StudentPreviewSource';
import EventTaskView from '@/student/module/components/task-types/event/EventTaskView';
import OralExamPrepWebLayout from '@/student/task/screenLayouts/OralExamPrepWebLayout';
import {
  buildOralExamTaskLabFixture,
  ORAL_EXAM_TASK_LAB_IDS,
  ORAL_EXAM_TASK_LAB_STATE_LABELS,
  ORAL_EXAM_TASK_LAB_STATE_NOTES,
  ORAL_EXAM_TASK_LAB_STATES,
  parseOralExamTaskLabState,
  type OralExamTaskLabState,
} from '@/visual-lab/chapters/oral-exam-task/oralExamTaskLabFixture';

export const ORAL_EXAM_TASK_LAB_PATH = '/visual-lab/oral-exam-task';

const SURFACES = ['preview', 'task'] as const;
type Surface = typeof SURFACES[number];
const SURFACE_LABELS: Record<Surface, string> = {
  preview: 'Module preview',
  task: 'Task page (desktop)',
};

/** Module preview column width on the student module page. */
const PREVIEW_WIDTH = 400;

function parseQuery(search: string): { state: OralExamTaskLabState; surface: Surface } {
  const params = new URLSearchParams(search);
  const surface = params.get('surface') === 'task' ? 'task' : 'preview';
  return { state: parseOralExamTaskLabState(params.get('state')), surface };
}

function buildSearch(state: OralExamTaskLabState, surface: Surface): string {
  return `?state=${state}&surface=${surface}`;
}

function seedTenantSettings() {
  const current = useTenantSettingsStore.getState().settings;
  useTenantSettingsStore.setState({
    settings: {
      ...current,
      contact: { ...current?.contact, supportEmail: 'studentservice@example.edu' },
    } as typeof current,
    status: 'success',
  });
}

function Chip({
  active, label, onPress, testID,
}: { active: boolean; label: string; onPress: () => void; testID: string }) {
  return (
    <Button
      onPress={onPress}
      size="xs"
      variant={active ? 'solid' : 'outline'}
      action={active ? 'primary' : 'secondary'}
      testID={testID}
    >
      <ButtonText>{label}</ButtonText>
    </Button>
  );
}

type OralExamTaskLabScreenProps = {
  search: string;
  onNavigate: (path: string) => void;
};

/**
 * Oral exam task lab — the production student oral-exam column and desktop task
 * page, fed one booking state at a time through the student-preview source.
 * Booking, cancelling and joining show the preview toast; the chips change state.
 */
export function OralExamTaskLabScreen({ search, onNavigate }: OralExamTaskLabScreenProps) {
  const { state, surface } = useMemo(() => parseQuery(search), [search]);
  const fixture = useMemo(() => buildOralExamTaskLabFixture(state), [state]);
  const queryClient = useMemo(() => new QueryClient({
    defaultOptions: { queries: { retry: false, refetchOnWindowFocus: false } },
  }), []);
  useMemo(seedTenantSettings, []);

  const go = (nextState: OralExamTaskLabState, nextSurface: Surface) => {
    onNavigate(`${ORAL_EXAM_TASK_LAB_PATH}${buildSearch(nextState, nextSurface)}`);
  };

  const selectedModule = {
    courseId: ORAL_EXAM_TASK_LAB_IDS.courseId,
    moduleId: ORAL_EXAM_TASK_LAB_IDS.moduleId,
  };

  return (
    <ScrollView flex={1} width="100%" bg="$bgApp">
      <VStack space="lg" px="$8" py="$6">
        <VStack space="xs">
          <Heading size="xl">Oral exam task</Heading>
          <Text size="sm" color="$textSubtle">
            The student oral-exam column and desktop task page, one booking state at a time. Buttons that
            would book, cancel or join show a preview toast instead.
          </Text>
        </VStack>

        <VStack space="sm">
          <HStack space="xs" flexWrap="wrap">
            {ORAL_EXAM_TASK_LAB_STATES.map((option) => (
              <Chip
                key={option}
                label={ORAL_EXAM_TASK_LAB_STATE_LABELS[option]}
                active={state === option}
                testID={`oral-exam-task-lab-state-${option}`}
                onPress={() => go(option, surface)}
              />
            ))}
          </HStack>
          <HStack space="xs">
            {SURFACES.map((option) => (
              <Chip
                key={option}
                label={SURFACE_LABELS[option]}
                active={surface === option}
                testID={`oral-exam-task-lab-surface-${option}`}
                onPress={() => go(state, option)}
              />
            ))}
          </HStack>
          <Text size="sm" color="$textBody" testID="oral-exam-task-lab-note">
            {ORAL_EXAM_TASK_LAB_STATE_NOTES[state]}
          </Text>
        </VStack>

        <QueryClientProvider client={queryClient}>
          <NavigationIndependentTree>
            <NavigationContainer>
              <StudentPreviewSourceProvider key={state} bookingState={fixture.bookingState}>
                {surface === 'preview' ? (
                  <Box
                    width={PREVIEW_WIDTH}
                    p="$5"
                    bg="$bgCard"
                    borderWidth={1}
                    borderColor="$borderLight"
                    borderRadius="$xl"
                    testID="oral-exam-task-lab-frame"
                  >
                    <EventTaskView
                      task={fixture.task}
                      module={fixture.module}
                      tasks={[fixture.task]}
                      selectedModule={selectedModule}
                      resources={[]}
                      showTitle={false}
                      noCard
                      handleOpenTask={() => go(state, 'task')}
                    />
                  </Box>
                ) : (
                  <Box
                    width={1200}
                    height={780}
                    borderWidth={1}
                    borderColor="$borderLight"
                    borderRadius="$xl"
                    overflow="hidden"
                    testID="oral-exam-task-lab-frame"
                  >
                    <OralExamPrepWebLayout
                      task={fixture.task}
                      module={fixture.module}
                      tasks={[fixture.task]}
                      resources={[]}
                      courseId={ORAL_EXAM_TASK_LAB_IDS.courseId}
                      moduleId={ORAL_EXAM_TASK_LAB_IDS.moduleId}
                      onBack={() => go(state, 'preview')}
                    />
                  </Box>
                )}
              </StudentPreviewSourceProvider>
            </NavigationContainer>
          </NavigationIndependentTree>
        </QueryClientProvider>
      </VStack>
    </ScrollView>
  );
}
